import React from "react";
import styled, { css } from "styled-components";
import {
  formatNumberWithCurrency,
  getSymbolForTickerUsingShortName,
  getAmountAndTickerFromInputSelector,
  getCharacterForDecimal,
  parseNumberStringToFloat,
  UNKNOWN_TICKER_SHORT_NAME,
  isCryptoCurrency,
  calcCustodianOwnershipValue
} from "@kubera/common";
import { useStore } from "react-redux";
import {
  cellMode,
  buildPasteStringWithCurrency,
  getGridCellPaddingRight,
  getGridCellContentOffsetLeft
} from "components/grid/GridCellExports";
import AnimatedNumber from "react-animated-number";
import CurrencyCellUpdateHandler from "./CurrencyCellUpdateHandler";

const Container = styled.div`
  display: flex;
  position: relative;
  flex: 1;
`;

const CurrencyContainer = styled.div`
  display: flex;
  position: relative;
  flex: 1;
  flex-direction: column;
  justify-content: ${props => (props.textVerticalAlignment === "top" ? "flex-start" : "center")};
  order: ${props => props.order};
`;

const CellCurrencyValue = styled.div`
  font-style: ${props => (props.updated === true ? "normal" : "italic")};
  font-weight: normal;
  font-size: 10px;
  line-height: 12px;
  text-align: ${props => (!props.textAlignment === false ? props.textAlignment : "right")};
  font-feature-settings: "ss01" on;
  color: ${props => (props.hasError === true ? "red" : props.theme.cellCurrencyValueCLR)};
  padding-right: ${getGridCellPaddingRight};
  padding-left: ${getGridCellContentOffsetLeft};
  z-index: 100;
  pointer-events: none;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  height: 12px;
  z-index: 100;
  width: 97px;
  direction: ${props => (window.safari ? "ltr" : "rtl")};

  ${props =>
    !props.subTextStyles === false
      ? css`
          ${props.subTextStyles}
        `
      : ""};
`;

const CellPreviousValue = styled(CellCurrencyValue)`
  text-decoration: line-through;
`;

const getChangeIndicatorColor = ({ change = 0, isDebt = false, theme }) => {
  if (!change === true) {
    return "";
  }

  if (isDebt === true) {
    return change > 0 ? theme.gridRedColorForLightBackground : theme.gridGreenColorForLightBackground;
  } else {
    return change > 0 ? theme.gridGreenColorForLightBackground : theme.gridRedColorForLightBackground;
  }
};
const ChangeIndicator = styled.div`
  position: absolute;
  right: 5px;
  top: ${props => (props.showingSubText === true ? "10px" : "17px")};
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-bottom: 6px solid ${props => getChangeIndicatorColor(props)};
  transform: ${props => (props.change >= 0 ? "" : "rotate(180deg)")};
`;

const LinkingAnimatedNumber = styled(AnimatedNumber)`
  display: flex;
  align-items: center;
  position: absolute;
  right: ${props => (!props.$textAlignment === true || props.$textAlignment === "right" ? 0 : "")};
  left: ${props => (!props.$textAlignment === false && props.$textAlignment === "left" ? 0 : "")};
  top: 0;
  bottom: 0;
  padding-right: ${getGridCellPaddingRight};
  padding-left: ${getGridCellContentOffsetLeft};
  font-size: 14px;
  line-height: 17px;
  font-feature-settings: "ss01" on;
  text-align: right;
  font-family: "Roboto Mono";
`;

class CurrencyCell extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      value: props.value,
      data: null,
      linkingAnimationNumber: this.getLinkingRandomNumber(),
      linkingAnimationDuration: 300
    };

    this.symbolForGridCurrency = getSymbolForTickerUsingShortName(props.store.getState(), props.gridCurrency);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handlePaste = this.handlePaste.bind(this);
    this.handleCopy = this.handleCopy.bind(this);
    this.cellCurrencyOnFocus = React.createRef(this.props.currency);

    this.animateNumberIfNeeded();
  }

  componentDidMount() {
    this.isComponentMounted = true;
  }

  componentWillUnmount() {
    if (!this.animationTimer === false) {
      clearInterval(this.animationTimer);
    }
    this.isComponentMounted = false;
  }

  componentDidUpdate(oldProps) {
    if (this.props.mode === cellMode.SELECT && this.state.value !== this.props.value) {
      this.setState({ value: this.props.value });
    }
    this.animateNumberIfNeeded();
  }

  animateNumberIfNeeded() {
    const animateNumber = () => {
      if (this.props.loading === true && !this.isComponentMounted === false) {
        const newRandomNumber = this.getLinkingRandomNumber();
        const delta = Math.abs(newRandomNumber - this.state.linkingAnimationNumber);
        const duration = delta * 50;

        this.setState({
          ...this.state,
          linkingAnimationNumber: newRandomNumber,
          linkingAnimationDuration: duration
        });
      } else if (!this.props.loading === true || this.isComponentMounted === false) {
        clearInterval(this.animationTimer);
        this.animationTimer = null;
      }
    };

    if (this.props.loading === true && !this.animationTimer === true) {
      this.animationTimer = setInterval(() => {
        animateNumber();
      }, 300);
      setTimeout(() => {
        animateNumber();
      }, 0);
    }
  }

  handlePaste(e) {
    const copiedText = e.clipboardData.getData("Text");
    const contentRows = copiedText.split(/\r\n|\n|\r/).filter(item => item.trim().length > 0);
    const columns = contentRows.length === 1 ? contentRows[0].split("\t") : [];
    if ((contentRows.length > 1 || columns.length > 1) && this.props.onPaste) {
      this.props.onPaste(e);
      return;
    }
    if (!this.props.isRowComplete) {
      return;
    }
    e.preventDefault();
    const newValue = buildPasteStringWithCurrency(
      this.props.store.getState(),
      copiedText,
      this.props.currency,
      this.props.gridCurrency
    );
    const inputValueDescriptor = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value");

    if (inputValueDescriptor && typeof inputValueDescriptor.set === "function") {
      inputValueDescriptor.set.call(e.target, newValue);
    } else {
      e.target.value = newValue;
    }
    e.target.dispatchEvent(new Event("input", { bubbles: true }));
  }

  handleInputChange(e) {
    if (this.props.multiCurrency === false) {
      var input = e.target.value;
      input = input.replace(getSymbolForTickerUsingShortName(this.props.store.getState(), this.props.currency), "");

      if (!parseNumberStringToFloat(this.props.store.getState(), input) === true) {
        return;
      }
    }

    const fallbackCurrency = this.props.mode === cellMode.EDIT ? this.props.gridCurrency : this.props.currency;
    const data = getAmountAndTickerFromInputSelector(this.props.store.getState(), e.target.value, fallbackCurrency);
    const maxAllowedDecimals =
      getCharacterForDecimal(this.props.store.getState()) === "." && data.ticker.shortName.includes(".") ? 2 : 1;

    if (
      e.target.value &&
      e.target.value.split(getCharacterForDecimal(this.props.store.getState())).length > maxAllowedDecimals + 1 &&
      e.target.value.charAt(e.target.value.length - 1) === getCharacterForDecimal(this.props.store.getState())
    ) {
      this.setState({ value: e.target.value.substring(0, e.target.value.length - 1), data: data });
      return;
    }

    this.setState({ value: e.target.value, data: data });

    if (this.props.mode !== cellMode.EDIT || this.props.callOnChangeEverytime === true) {
      this.props.onChange(data.value, data.isTickerInputEmpty || data.isTickerInputValid ? data.ticker : null, e, true);
    }
  }

  handleNullValueOnBlur = async () => {
    return new Promise(resolve => {
      if (this.props.disallowEmptyValue === true && [null, "", undefined].includes(this.state.value)) {
        const data = getAmountAndTickerFromInputSelector(
          this.props.store.getState(),
          "0",
          this.cellCurrencyOnFocus.current
        );
        this.setState({ value: "0", data: data }, () => {
          resolve();
        });
      } else {
        resolve();
      }
    });
  };

  getNumericStartIndex(value) {
    const currencyLength = value.includes(this.props.currency)
      ? this.props.currency.length
      : value.includes(this.props.gridCurrency)
      ? this.props.gridCurrency.length
      : value.includes(this.cellCurrencyOnFocus.current)
      ? this.cellCurrencyOnFocus.current.length
      : 0;
    const match = value.match(/[-0-9]/);
    if (match) {
      return value.indexOf(match[0]);
    }
    return value.charAt(currencyLength) === " " ? currencyLength + 1 : currencyLength;
  }

  enforceCaretPosition = e => {
    const input = e.target;
    if (this.props.mode !== cellMode.EDIT || !this.props.currency || this.props.enterEditModeOnClick) {
      return;
    }
    const minPos = this.getNumericStartIndex(input.value);
    if (minPos > 0 && input.selectionStart < minPos) {
      input.setSelectionRange(minPos, Math.max(minPos, input.selectionEnd));
    }
    if (e.key === "Backspace" && input.selectionStart <= minPos && input.selectionStart === input.selectionEnd) {
      e.preventDefault();
      return;
    }
    if (e.key === "Delete" && input.selectionStart < minPos) {
      e.preventDefault();
      return;
    }
  };

  handleFocus = e => {
    this.cellCurrencyOnFocus.current = this.props.currency;
    this.props.onFocus(e);
  };

  handleKeyDown = async e => {
    this.enforceCaretPosition(e);
    if (e.key === "Enter" && [cellMode.EDIT, cellMode.UPDATE].includes(this.props.mode)) {
      await this.handleNullValueOnBlur();
      const data = this.state.data;
      if (!data === false) {
        this.props.onChange(
          data.value,
          data.isTickerInputEmpty || data.isTickerInputValid ? data.ticker : null,
          e,
          true
        );
      }
      this.setState({ ...this.state, data: null });
    }

    this.props.onKeyDown(e);
  };

  handleBlur = async e => {
    if ([cellMode.EDIT, cellMode.UPDATE].includes(this.props.mode)) {
      await this.handleNullValueOnBlur();
      const data = this.state.data;
      if (!data === false) {
        this.props.onChange(
          data.value,
          data.isTickerInputEmpty || data.isTickerInputValid ? data.ticker : null,
          e,
          true
        );
      }
    }

    this.setState({ ...this.state, data: null });
    this.props.onBlur(e);
  };

  handleClick = e => {
    this.enforceCaretPosition(e);
    this.props.onClick(e);
  };

  handleMouseUp = e => {
    // Single-clicking a SELECT-mode value cell should highlight the whole value so the next
    // keystroke replaces it. The focus-driven selectAllText() defers via setTimeout(0), which
    // can fire before the browser's mouseup caret placement and get collapsed -- so the selection
    // is lost intermittently and typing inserts mid-value (e.g. "$14,180" -> "AUD 11809214").
    // mouseup runs after caret placement, so re-assert the selection here and preventDefault the
    // collapse. enterEditModeOnClick cells intentionally place the caret (see enforceCaretPosition).
    if (this.props.mode === cellMode.SELECT && !this.props.enterEditModeOnClick && e.button === 0) {
      e.preventDefault();
      e.currentTarget.select();
    }
  };

  handleCopy(e) {
    if (this.props.mode === cellMode.SELECT) {
      const displayString = this.getDisplayString(
        this.props.value,
        this.props.currency,
        this.props.gridCurrency,
        cellMode.EDIT
      );
      e.clipboardData.setData("text/plain", displayString);
      e.preventDefault();
    }
  }

  getDisplayString(value, currency, gridCurrency, mode) {
    if (!this.props.invalidInputText === false && mode === cellMode.SELECT) {
      return this.props.invalidInputText;
    }
    if (currency === UNKNOWN_TICKER_SHORT_NAME) {
      return this.props.invalidInputText;
    }

    const data = this.state.data;
    if (mode === cellMode.EDIT) {
      if (!this.props.invalidInputText === false) {
        return this.props.value === this.state.value ? this.props.invalidInputText : this.state.value;
      }
      if (this.props.isInSingleCellMode === true && !this.props.value === true && !this.state.value === false) {
        return formatNumberWithCurrency(this.props.store.getState(), this.state.value, gridCurrency, false);
      }
      if (this.props.value !== this.state.value) {
        try {
          if (!this.props.enterEditModeOnClick && (value === "-" || !isNaN(Number(value)))) {
            return (this.cellCurrencyOnFocus.current || data.ticker.shortName) + " " + (value ?? "");
          }
          return value;
        } catch (error) {
          return value;
        }
      }
    } else if (mode === cellMode.UPDATE) {
      // If the user is updating the cell and trying to update currency
      // remove the current currency symbol so that the new input
      // can be parsed correctly
      if (data && data.isTickerInputValid === false) {
        return value.replace(getSymbolForTickerUsingShortName(this.props.store.getState(), currency), "");
      }
    }

    if (isNaN(value) === true) {
      return value;
    }
    if (value === null || value === undefined || value === "") {
      return "";
    }

    var applicableCurrency = currency;
    if (mode === cellMode.SELECT) {
      applicableCurrency = gridCurrency;
    }

    var applicableValue =
      value * this.props.gridCell.getCellExchangeRate(this.props.store.getState(), applicableCurrency);
    if (mode === cellMode.SELECT) {
      if (isCryptoCurrency(this.props.store.getState(), gridCurrency) === false) {
        applicableValue =
          (applicableValue > 0 && applicableValue < 1) || this.props.showDecimal
            ? Number.isInteger(this.props.decimalPlaces)
              ? applicableValue.toFixed(this.props.decimalPlaces)
              : applicableValue.toFixed(2)
            : Math.kuberaFloor(applicableValue);
      }
    } else if (mode === cellMode.EDIT) {
      applicableValue = value;
    }

    return mode === cellMode.SELECT || this.props.decimalPlaces > 2
      ? formatNumberWithCurrency(
          this.props.store.getState(),
          applicableValue,
          applicableCurrency,
          mode === cellMode.SELECT,
          mode === cellMode.SELECT ? (this.props.skipFiatDecimalClamp === true ? this.props.decimalPlaces : 4) : 8,
          mode !== cellMode.SELECT,
          false,
          true,
          true,
          true,
          false,
          false,
          false,
          this.props.skipFiatDecimalClamp === true
        )
      : formatNumberWithCurrency(
          this.props.store.getState(),
          applicableValue,
          applicableCurrency,
          mode === cellMode.SELECT,
          mode === cellMode.SELECT ? 4 : 8,
          mode !== cellMode.SELECT
        );
  }

  isCellCurrencyDifferent() {
    return this.props.currency !== this.props.gridCurrency;
  }

  getNetValue(value, ownership, gridCurrency) {
    const valueAfterOwnership = calcCustodianOwnershipValue(value, ownership);
    if (!this.props.gridCell?.adjustments?.length === true || value === null || value === "" || isNaN(value)) {
      return valueAfterOwnership;
    }
    return (
      valueAfterOwnership - this.props.gridCell.getAdjustmentInCellCurrency(this.props.store.getState(), gridCurrency)
    );
  }

  hasAdjustment(gridCurrency) {
    return (
      !this.props.gridCell?.adjustments?.length === false &&
      this.props.gridCell.getAdjustmentInCellCurrency(this.props.store.getState(), gridCurrency) !== 0
    );
  }

  doesUserHasFullOwnership() {
    const ownership = parseInt(this.props.ownership, 10);
    if (!this.props.ownership === true && ownership !== 0) {
      return true;
    }
    return ownership === 100;
  }

  getLinkingRandomNumber() {
    return Math.floor(Math.random() * (90000 - 10000 + 1)) + 10000;
  }

  render() {
    const {
      className,
      type,
      onChange,
      currency,
      gridCurrency,
      onBlur,
      loading,
      change,
      isDebt,
      textAlignment,
      textVerticalAlignment,
      accessoryView,
      hasError,
      ownership,
      isEditable,
      previousValue,
      previousCurrency,
      hasChangeIndicator,
      isLeadingCell,
      isSingleCellRow,
      ...other
    } = this.props;

    if (loading === true) {
      return (
        <Container className={this.props.className} id={this.props.id} tabIndex={0}>
          <LinkingAnimatedNumber
            stepPrecision={0}
            $textAlignment={textAlignment}
            $hasChangeIndicator={hasChangeIndicator}
            $isLeadingCell={isLeadingCell}
            $isSingleCellRow={isSingleCellRow}
            value={this.state.linkingAnimationNumber}
            duration={this.state.linkingAnimationDuration}
            formatValue={n =>
              this.symbolForGridCurrency === this.props.gridCurrency
                ? this.symbolForGridCurrency + " " + n
                : this.symbolForGridCurrency + n
            }
          />
        </Container>
      );
    }

    const value = this.props.mode === cellMode.SELECT ? this.props.value : this.state.value;
    const displayString = this.getDisplayString(
      this.props.mode === cellMode.SELECT ? this.getNetValue(value, ownership, gridCurrency) : value,
      currency,
      gridCurrency,
      this.props.mode
    );
    const showCellCurrencyValue =
      value &&
      value !== 0 &&
      !this.props.invalidInputText === true &&
      this.props.mode === cellMode.SELECT &&
      (this.isCellCurrencyDifferent() || !this.doesUserHasFullOwnership() || this.hasAdjustment(gridCurrency));
    const cellCurrencyValue =
      showCellCurrencyValue === false
        ? ""
        : formatNumberWithCurrency(
            this.props.store.getState(),
            value,
            currency,
            false,
            undefined,
            false,
            false,
            true,
            true,
            true,
            true,
            true
          );
    const cellPreviousCurrencyValue =
      previousValue && previousCurrency && (value !== previousValue || currency !== previousCurrency)
        ? formatNumberWithCurrency(
            this.props.store.getState(),
            previousValue,
            previousCurrency,
            false,
            undefined,
            false,
            false,
            true,
            true,
            true,
            true,
            true
          )
        : "";
    const inputProps = {
      other: other,
      className: className,
      type: "text",
      value: displayString === null || displayString === undefined ? "" : displayString,
      onChange: this.handleInputChange,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      onKeyDown: this.handleKeyDown,
      onClick: this.handleClick,
      onMouseUp: this.handleMouseUp,
      onSelect: this.enforceCaretPosition,
      spellCheck: "false",
      onPaste: this.handlePaste,
      onCopy: this.handleCopy
    };

    return (
      <Container>
        <CurrencyContainer order={this.props.order} textVerticalAlignment={textVerticalAlignment}>
          <CurrencyCellUpdateHandler
            className={className}
            value={
              displayString === null || displayString === undefined
                ? this.props.emptyValueIndicator || ""
                : displayString
            }
            isEditing={[cellMode.EDIT].includes(this.props.mode)}
            inputProps={inputProps}
            isEditable={isEditable}
          />
          {showCellCurrencyValue === true && (
            <CellCurrencyValue
              subTextStyles={this.props.cellSubTextStyles}
              updated={this.props.updated}
              textAlignment={textAlignment}
              hasError={hasError}
              hasChangeIndicator={hasChangeIndicator}
              isLeadingCell={isLeadingCell}
              isSingleCellRow={isSingleCellRow}
            >
              &lrm;
              {cellCurrencyValue}
            </CellCurrencyValue>
          )}
          {!cellPreviousCurrencyValue === false && this.props.mode === cellMode.SELECT && (
            <CellPreviousValue
              subTextStyles={this.props.cellSubTextStyles}
              updated={this.props.updated}
              textAlignment={textAlignment}
              hasError={hasError}
              hasChangeIndicator={hasChangeIndicator}
              isLeadingCell={isLeadingCell}
              isSingleCellRow={isSingleCellRow}
            >
              &lrm;
              {cellPreviousCurrencyValue}
            </CellPreviousValue>
          )}
          {!change === false && this.props.mode === cellMode.SELECT && (
            <ChangeIndicator isDebt={this.props.isDebt} change={change} showingSubText={showCellCurrencyValue} />
          )}
        </CurrencyContainer>
        {accessoryView}
      </Container>
    );
  }
}

const CurrencyCellWrapper = props => {
  const store = useStore();
  return <CurrencyCell {...props} store={store} />;
};

export default CurrencyCellWrapper;
